/**
 * Tool registration and handler dispatch
 */

import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import { resolveEnvironmentInput } from "./environment-resolution.js";
import type { ConnectorMetadataApi } from "../api/connector-metadata.js";
import type { GraphApi } from "../api/graph-api.js";
import type { PowerAppsApi } from "../api/powerapps-api.js";
import {
  CanvasAuthoringError,
  type CanvasAuthoringClientLike,
} from "../api/canvas-authoring-client.js";
import type { PowerPlatformAdminApi } from "../api/power-platform-admin-api.js";
import type { PowerPlatformInventoryApi } from "../api/power-platform-inventory-api.js";
import {
  CopilotComponentUpdateError,
  CopilotConnectionReferenceUpdateError,
  type DataverseApi,
} from "../api/dataverse-api.js";
import type { ModelDrivenApi } from "../api/model-driven-api.js";
import type { DesktopFlowsApi } from "../api/desktop-flows-api.js";
import type { ConnectionsApi } from "../api/connections-api.js";
import type { PowerPagesApi } from "../api/powerpages-api.js";
import type { SchemaCache } from "../cache/schema-cache.js";
import { FEATURE_LABELS, type FeatureId, normalizeFeatures } from "../config/features.js";
import { logger } from "../utils/logger.js";
import {
  AuthenticationError,
  AuthorizationError,
  ValidationError,
  ConfirmationRequiredError,
  isPowerAutomateError,
} from "../utils/errors.js";
import { sanitizeErrorMessage } from "../utils/security.js";
import { extractArmErrorMessage, ERROR_DETAIL_LIMIT } from "../utils/format.js";
import { consumeUpdateNotice, getInstalledVersion } from "../utils/update-checker.js";

// Phase 1 tools
import { listFlowsTool, handleListFlows } from "./list-flows.js";
import { getFlowTool, handleGetFlow } from "./get-flow.js";
import { getRunsTool, handleGetRuns } from "./get-runs.js";
import { toggleFlowTool, handleToggleFlow } from "./toggle-flow.js";

// Phase 2 tools
import { searchConnectorsTool, handleSearchConnectors } from "./search-connectors.js";
import { getActionSchemaTool, handleGetActionSchema } from "./get-action-schema.js";

// Phase 3 tools
import { createFlowTool, handleCreateFlow } from "./create-flow.js";
import { updateFlowTool, handleUpdateFlow } from "./update-flow.js";
import { previewUpdateTool, handlePreviewUpdate } from "./preview-update.js";
import { flowHistoryTools, flowHistoryHandlers } from "./flow-history-tools.js";
import { cancelAllRunsTool, handleCancelAllRuns } from "./cancel-all-runs.js";
import { deleteFlowTool, handleDeleteFlow } from "./delete-flow.js";
import { validateFlowTool, handleValidateFlow } from "./validate-flow.js";
import { cloneFlowTool, handleCloneFlow } from "./clone-flow.js";
import { runFlowTool, handleRunFlow } from "./run-flow.js";

// Phase 4 tools - Connections, Diagnostics, Sharing
import { listConnectionsTool, handleListConnections } from "./list-connections.js";
import {
  connectionLifecycleTools,
  connectionLifecycleHandlers,
} from "./connection-lifecycle-tools.js";
import { getRunActionsTool, handleGetRunActions } from "./get-run-actions.js";
import {
  getRunActionRepetitionsTool,
  handleGetRunActionRepetitions,
} from "./get-run-action-repetitions.js";
import { getFlowPermissionsTool, handleGetFlowPermissions } from "./get-flow-permissions.js";
import { shareFlowTool, handleShareFlow } from "./share-flow.js";

// Phase 5 tools - Run Control, Environment Management
import { resubmitRunTool, handleResubmitRun } from "./resubmit-run.js";
import { cancelRunTool, handleCancelRun } from "./cancel-run.js";
import { listEnvironmentsTool, handleListEnvironments } from "./list-environments.js";
import {
  switchEnvironmentTool,
  handleSwitchEnvironment,
  type EnvironmentSession,
} from "./switch-environment.js";
import type { AuthProvider } from "../auth/types.js";
import {
  CopilotKnowledgeFileReadinessApi,
  CopilotKnowledgeFileReadinessError,
  getCopilotKnowledgeFileReadinessInputSchema,
} from "../api/copilot-knowledge-file-readiness-api.js";
import {
  getCopilotKnowledgeFileReadinessTool,
  handleGetCopilotKnowledgeFileReadiness,
} from "./copilot-knowledge-file-readiness-tools.js";
import {
  CopilotKnowledgeFileApi,
  CopilotKnowledgeFileError,
  createCopilotKnowledgeTextFileInputSchema,
} from "../api/copilot-knowledge-file-api.js";
import {
  createCopilotKnowledgeTextFileTool,
  handleCreateCopilotKnowledgeTextFile,
} from "./copilot-knowledge-file-tools.js";

// Phase 6 tools - Versions, Export, Approvals
import { listFlowVersionsTool, handleListFlowVersions } from "./list-flow-versions.js";
import { exportFlowTool, handleExportFlow } from "./export-flow.js";
import { listApprovalsTool, handleListApprovals } from "./list-approvals.js";
import { respondApprovalTool, handleRespondApproval } from "./respond-approval.js";

// Interactive planning and troubleshooting tools
import { planFlowTool, handlePlanFlow } from "./plan-flow.js";
import { diagnoseFlowTool, handleDiagnoseFlow } from "./diagnose-flow.js";
import { buildFlowTool, handleBuildFlow } from "./build-flow.js";

// Custom connector tools
import {
  listCustomConnectorsTool,
  diagnoseConnectorAuthTool,
  handleDiagnoseConnectorAuth,
  updateConnectorOauthScopesTool,
  handleUpdateConnectorOauthScopes,
  handleListCustomConnectors,
  getCustomConnectorTool,
  handleGetCustomConnector,
  createCustomConnectorTool,
  handleCreateCustomConnector,
  updateCustomConnectorTool,
  handleUpdateCustomConnector,
  deleteCustomConnectorTool,
  handleDeleteCustomConnector,
  importOpenApiTool,
  handleImportOpenApi,
  planCustomConnectorTool,
  handlePlanCustomConnector,
} from "./custom-connectors.js";

// Excel helper tools
import {
  searchExcelFilesTool,
  handleSearchExcelFiles,
  inspectExcelFileTool,
  handleInspectExcelFile,
} from "./excel-helpers.js";

// Power Apps tools
import {
  powerAppsTools,
  handleListPowerApps,
  handleGetPowerApp,
  handleDeletePowerApp,
  handlePublishPowerApp,
  handleGetPowerAppVersions,
  handleRestorePowerAppVersion,
  handleSetPowerAppDisplayName,
  handleGetPowerAppPermissions,
  handleSharePowerApp,
  handleUnsharePowerApp,
  handleListPowerAppsAdmin,
  handleGetPowerAppAdmin,
  handleDeletePowerAppAdmin,
  handleSetPowerAppOwner,
  handleQuarantinePowerApp,
} from "./powerapps-tools.js";

// Canvas Apps source authoring (official Microsoft preview MCP bridge)
import { canvasAuthoringTools, canvasAuthoringHandlers } from "./canvas-authoring-tools.js";

// Model-driven Power Apps (Dataverse AppModule lifecycle)
import {
  modelDrivenTools,
  handleListModelDrivenApps,
  handleGetModelDrivenApp,
  handleCreateModelDrivenApp,
  handleUpdateModelDrivenApp,
  handleDeleteModelDrivenApp,
  handleGetModelDrivenAppComponents,
  handleAddModelDrivenAppComponents,
  handleRemoveModelDrivenAppComponents,
  handleValidateModelDrivenApp,
  handlePublishModelDrivenApp,
  handleListModelDrivenAppRoles,
  handleGrantModelDrivenAppRole,
  handleRevokeModelDrivenAppRole,
} from "./model-driven-tools.js";

// Admin tools (environment lifecycle, DLP, managed envs, capacity)
import {
  adminTools,
  handleGetEnvironment,
  handleCreateEnvironment,
  handleDeleteEnvironment,
  handleBackupEnvironment,
  handleRestoreEnvironment,
  handleCopyEnvironment,
  handleResetEnvironment,
  handleListEnvironmentBackups,
  handleListDlpPolicies,
  handleGetDlpPolicy,
  handleCreateDlpPolicy,
  handleUpdateDlpPolicy,
  handleDeleteDlpPolicy,
  handleGetDlpConnectorConfigs,
  handleGetManagedEnvironmentSettings,
  handleEnableManagedEnvironment,
  handleDisableManagedEnvironment,
  handleUpdateManagedEnvironmentSettings,
  handleGetTenantCapacity,
  handleGetEnvironmentCapacity,
  handleListBillingPolicies,
  handleGetBillingPolicy,
  handleGetApiRequestSummary,
} from "./admin-tools.js";

// Solution ALM tools
import {
  solutionTools,
  handleExportSolution,
  handleImportSolution,
  handlePublishAllCustomizations,
  handleCreateSolution,
  handleDeleteSolution,
  handleAddSolutionComponent,
  handleRemoveSolutionComponent,
  handleCloneSolution,
} from "./solution-tools.js";

// Test flow tool
import { testFlowTool, handleTestFlow } from "./test-flow.js";

// Expression help tool
import { expressionHelpTool, handleExpressionHelp } from "./expression-help.js";

// Dataverse tools (solutions, canvas apps, AI models, approvals via Dataverse)
import {
  listSolutionsTool,
  handleListSolutions,
  listSolutionFlowsTool,
  handleListSolutionFlows,
  listCanvasAppsTool,
  handleListCanvasApps,
  listAIModelsTool,
  handleListAIModels,
  listApprovalsDataverseTool,
  handleListApprovalsDataverse,
} from "./dataverse-tools.js";

// Dataverse table metadata tools
import {
  listDataverseTablesTool,
  handleListDataverseTables,
  getDataverseTableTool,
  handleGetDataverseTable,
} from "./dataverse-table-tools.js";

// Dataverse row CRUD tools
import {
  queryDataverseRowsTool,
  handleQueryDataverseRows,
  getDataverseRowTool,
  handleGetDataverseRow,
  createDataverseRowTool,
  handleCreateDataverseRow,
  updateDataverseRowTool,
  handleUpdateDataverseRow,
  deleteDataverseRowTool,
  handleDeleteDataverseRow,
} from "./dataverse-row-tools.js";

// Dataverse depth tools (FetchXML, search, metadata, schema write, batch)
import {
  executeFetchXmlTool,
  handleExecuteFetchXml,
  searchDataverseTool,
  handleSearchDataverse,
  getDataverseOptionSetTool,
  handleGetDataverseOptionSet,
  getDataverseRelationshipsTool,
  handleGetDataverseRelationships,
  upsertDataverseRowTool,
  handleUpsertDataverseRow,
  assignDataverseRowTool,
  handleAssignDataverseRow,
  associateDataverseRowsTool,
  handleAssociateDataverseRows,
  disassociateDataverseRowsTool,
  handleDisassociateDataverseRows,
  createDataverseColumnTool,
  handleCreateDataverseColumn,
  createDataverseLookupColumnTool,
  handleCreateDataverseLookupColumn,
  batchDataverseOperationsTool,
  handleBatchDataverseOperations,
} from "./dataverse-depth-tools.js";
// Copilot Studio agent tools (agents are Dataverse bot/botcomponent rows)
import {
  listCopilotAgentsTool,
  handleListCopilotAgents,
  getCopilotAgentTool,
  handleGetCopilotAgent,
  getCopilotAgentComponentTool,
  handleGetCopilotAgentComponent,
  listCopilotAgentComponentsTool,
  handleListCopilotAgentComponents,
  createCopilotAgentTool,
  handleCreateCopilotAgent,
  cloneCopilotAgentTool,
  handleCloneCopilotAgent,
  addCopilotAgentToolTool,
  handleAddCopilotAgentTool,
  configureCopilotAgentTool,
  handleConfigureCopilotAgent,
  setCopilotAgentAccessTool,
  handleSetCopilotAgentAccess,
  setCopilotAgentIconTool,
  handleSetCopilotAgentIcon,
  addCopilotAgentKnowledgeTool,
  handleAddCopilotAgentKnowledge,
  addCopilotAgentTopicTool,
  handleAddCopilotAgentTopic,
  addCopilotAgentTestCaseTool,
  addCopilotAgentEvaluationTestSetTool,
  handleAddCopilotAgentEvaluationTestSet,
  handleAddCopilotAgentTestCase,
  getCopilotAgentTranscriptsTool,
  handleGetCopilotAgentTranscripts,
  setCopilotAgentInstructionsTool,
  handleSetCopilotAgentInstructions,
  upsertCopilotAgentComponentTool,
  handleUpsertCopilotAgentComponent,
  publishCopilotAgentTool,
  handlePublishCopilotAgent,
  getCopilotAgentEndpointTool,
  handleGetCopilotAgentEndpoint,
  chatWithCopilotAgentTool,
  handleChatWithCopilotAgent,
  auditCopilotAgentAccessTool,
  handleAuditCopilotAgentAccess,
  shareCopilotAgentTool,
  handleShareCopilotAgent,
  deleteCopilotAgentTool,
  handleDeleteCopilotAgent,
  deleteCopilotAgentComponentTool,
  handleDeleteCopilotAgentComponent,
  validateCopilotAgentTool,
  handleValidateCopilotAgent,
  listCopilotAgentToolsTool,
  handleListCopilotAgentTools,
} from "./copilot-studio-tools.js";
import {
  rebindCopilotAgentToolConnectionTool,
  handleRebindCopilotAgentToolConnection,
} from "./rebind-copilot-agent-tool-connection.js";
import {
  listCopilotComponentCollectionsTool,
  handleListCopilotComponentCollections,
  getCopilotComponentCollectionTool,
  handleGetCopilotComponentCollection,
} from "./copilot-collection-tools.js";
import {
  getCopilotKnowledgeIndexStatusTool,
  handleGetCopilotKnowledgeIndexStatus,
  getCopilotKnowledgeIndexStatisticsTool,
  handleGetCopilotKnowledgeIndexStatistics,
} from "./copilot-knowledge-index-tools.js";
import {
  getCopilotAgentPermissionsTool,
  handleGetCopilotAgentPermissions,
} from "./copilot-agent-permissions.js";
import {
  getCopilotEvaluationConnectionsTool,
  handleGetCopilotEvaluationConnections,
} from "./copilot-evaluation-connections-tools.js";
import { CopilotEvaluationConnectionsError, getSafeCopilotEvaluationConnectionsErrorMessage } from "../api/copilot-evaluation-connections-api.js";
import { getSafeCopilotConversationTestCaseErrorMessage } from "./copilot-conversation-test-case.js";
import {
  CopilotMcpConnectorApi,
  CopilotMcpConnectorCreateError,
} from "../api/copilot-mcp-connector-api.js";
import {
  createCopilotMcpConnectorTool,
  handleCreateCopilotMcpConnector,
} from "./copilot-mcp-connector-tools.js";
import {
  CopilotStudioEvaluationDetailsApi,
  CopilotStudioEvaluationDetailsError,
} from "../api/copilot-studio-evaluation-details-api.js";
import { EnvironmentApi } from "../api/environment.js";
import {
  CopilotPermissionCatalogApi,
  CopilotPermissionCatalogError,
} from "../api/copilot-permission-catalog-api.js";
import {
  getCopilotPermissionCatalogTool,
  handleGetCopilotPermissionCatalog,
} from "./copilot-permission-catalog-tools.js";
import {
  getCopilotAgentChannelConfigurationTool,
  handleGetCopilotAgentChannelConfiguration,
} from "./copilot-channel-configuration-tools.js";
import { CopilotActivityApi, CopilotActivityError } from "../api/copilot-activity-api.js";
import {
  listCopilotAgentActivityTool,
  handleListCopilotAgentActivity,
} from "./copilot-activity-tools.js";
import {
  CopilotActivityDetailApi,
  CopilotActivityDetailError,
} from "../api/copilot-activity-detail-api.js";
import {
  getCopilotAgentActivityTool,
  handleGetCopilotAgentActivity,
} from "./copilot-activity-detail-tools.js";
import { CopilotMonitorApi, CopilotMonitorError } from "../api/copilot-monitor-api.js";
import {
  getCopilotAgentSessionSummaryTool,
  handleGetCopilotAgentSessionSummary,
} from "./copilot-monitor-tools.js";
import {
  getCopilotStudioEvaluationDetailsTool,
  handleGetCopilotStudioEvaluationDetails,
} from "./copilot-studio-evaluation-details-tools.js";
import {
  downloadCopilotAgentChannelManifestTool,
  handleDownloadCopilotAgentChannelManifest,
} from "./copilot-channel-tools.js";
import { CopilotChannelApi } from "../api/copilot-channel-api.js";
import {
  getCopilotAgentInventoryTool,
  handleGetCopilotAgentInventory,
} from "./copilot-inventory-tools.js";
import {
  copilotEvaluationTools,
  handleDownloadCopilotEvaluationSnapshot,
  handleGetCopilotEvaluationRun,
  handleGetCopilotEvaluationTestSet,
  handleListCopilotEvaluationRuns,
  handleListCopilotEvaluationTestSets,
  handleStartCopilotEvaluation,
} from "./copilot-evaluation-tools.js";
import {
  formatCopilotEvaluationHttpError,
  type CopilotEvaluationApi,
} from "../api/copilot-evaluation-api.js";
import { executeCopilotAgentTool, handleExecuteCopilotAgent } from "./copilot-execution-tools.js";
import type { CopilotStudioExecutionApi } from "../api/copilot-studio-execution-api.js";
import {
  copilotAdminTools,
  handleGetCopilotAgentConnectorConsentBypass,
  handleGetCopilotAgentQuarantine,
  handleReassignCopilotAgentOwner,
  handleSetCopilotAgentConnectorConsentBypass,
  handleSetCopilotAgentQuarantine,
} from "./copilot-admin-tools.js";
import type { CopilotAdminApi } from "../api/copilot-admin-api.js";

// Power Pages tools (Dataverse config + management)
import {
  powerPagesTools,
  additionalPowerPagesManagementTools,
  pacPagesTools,
  powerPagesManagementHandlers,
  pacPagesHandlers,
  handleListPowerPagesSites,
  handleGetPowerPagesSite,
  handleListPowerPagesComponents,
  handleGetPowerPagesComponent,
  handleCreatePowerPagesComponent,
  handleUpdatePowerPagesComponent,
  handleDeletePowerPagesComponent,
  handleManagePowerPagesRelationship,
  handleUploadPowerPagesWebFileContent,
  handleListPowerPagesWebsites,
  handleGetPowerPagesWebsite,
  handleCreatePowerPagesWebsite,
  handleDeletePowerPagesWebsite,
  handleRestartPowerPagesWebsite,
} from "./powerpages-tools.js";

// SharePoint tools (sites, lists, items)
import {
  searchSharePointSitesTool,
  handleSearchSharePointSites,
  getSharePointSiteTool,
  handleGetSharePointSite,
  listSharePointListsTool,
  handleListSharePointLists,
  getSharePointListColumnsTool,
  handleGetSharePointListColumns,
  listSharePointItemsTool,
  handleListSharePointItems,
  createSharePointItemTool,
  handleCreateSharePointItem,
  updateSharePointItemTool,
  handleUpdateSharePointItem,
  deleteSharePointItemTool,
  handleDeleteSharePointItem,
} from "./sharepoint-tools.js";

// SharePoint file tools
import {
  listSharePointFilesTool,
  handleListSharePointFiles,
  uploadSharePointFileTool,
  handleUploadSharePointFile,
  getSharePointFileContentTool,
  handleGetSharePointFileContent,
} from "./sharepoint-file-tools.js";

// Desktop flows tools
import {
  listMachinesTool,
  handleListMachines,
  getMachineTool,
  handleGetMachine,
  listMachineGroupsTool,
  handleListMachineGroups,
  restartHostedMachineTool,
  handleRestartHostedMachine,
  listDesktopFlowsTool,
  handleListDesktopFlows,
  getDesktopFlowTool,
  handleGetDesktopFlow,
  runDesktopFlowTool,
  handleRunDesktopFlow,
  getDesktopFlowRunsTool,
  handleGetDesktopFlowRuns,
  getDesktopFlowRunTool,
  handleGetDesktopFlowRun,
  cancelDesktopFlowRunTool,
  handleCancelDesktopFlowRun,
  listDesktopFlowConnectionsTool,
  handleListDesktopFlowConnections,
  getDesktopFlowRunLogsTool,
  handleGetDesktopFlowRunLogs,
  diagnoseDesktopFlowRunTool,
  handleDiagnoseDesktopFlowRun,
} from "./desktop-flows-tools.js";
import { workQueueTools, workQueueHandlers } from "./work-queues-tools.js";
import { signInTool, handleSignIn } from "./sign-in.js";
import { pacCopilotTools, pacCopilotHandlers } from "./copilot-pac-tools.js";
import { annotateTools, isDestructiveToolName } from "./annotations.js";

/**
 * All registered tools
 */
export const tools: Tool[] = [
  // Authentication (always available — how MCPB installs sign in without a terminal)
  signInTool,
  ...pacCopilotTools,
  // Phase 1: Core flow management
  listFlowsTool,
  getFlowTool,
  getRunsTool,
  toggleFlowTool,
  // Phase 2: Connector intelligence
  searchConnectorsTool,
  getActionSchemaTool,
  // Phase 3: Flow authoring
  createFlowTool,
  updateFlowTool,
  previewUpdateTool,
  cancelAllRunsTool,
  ...flowHistoryTools,
  deleteFlowTool,
  validateFlowTool,
  cloneFlowTool,
  runFlowTool,
  // Phase 4: Connections, Diagnostics, Sharing
  listConnectionsTool,
  ...connectionLifecycleTools,
  getRunActionsTool,
  getRunActionRepetitionsTool,
  getFlowPermissionsTool,
  shareFlowTool,
  // Phase 5: Run Control, Environment Management
  resubmitRunTool,
  cancelRunTool,
  listEnvironmentsTool,
  switchEnvironmentTool,
  // Phase 6: Versions, Export, Approvals
  listFlowVersionsTool,
  exportFlowTool,
  listApprovalsTool,
  respondApprovalTool,
  // Interactive planning and troubleshooting
  planFlowTool,
  diagnoseFlowTool,
  buildFlowTool,
  // Custom connector management
  listCustomConnectorsTool,
  diagnoseConnectorAuthTool,
  updateConnectorOauthScopesTool,
  getCustomConnectorTool,
  createCustomConnectorTool,
  updateCustomConnectorTool,
  deleteCustomConnectorTool,
  importOpenApiTool,
  planCustomConnectorTool,
  // Excel helpers
  searchExcelFilesTool,
  inspectExcelFileTool,
  // Power Apps management
  ...powerAppsTools,
  // Canvas Apps source authoring (preview)
  ...canvasAuthoringTools,
  // Model-driven Power Apps (Dataverse)
  ...modelDrivenTools,
  // Admin tools (environment lifecycle, DLP, managed envs, capacity)
  ...adminTools,
  // Solution ALM
  ...solutionTools,
  // Test flow
  testFlowTool,
  // Expression help
  expressionHelpTool,
  // Dataverse tools (solutions, canvas apps, AI models, approvals)
  listSolutionsTool,
  listSolutionFlowsTool,
  listCanvasAppsTool,
  listAIModelsTool,
  listApprovalsDataverseTool,
  // Dataverse table metadata
  listDataverseTablesTool,
  getDataverseTableTool,
  // Dataverse row CRUD
  queryDataverseRowsTool,
  getDataverseRowTool,
  createDataverseRowTool,
  updateDataverseRowTool,
  deleteDataverseRowTool,
  // Dataverse depth
  // Copilot Studio agents
  getCopilotKnowledgeIndexStatusTool,
  getCopilotKnowledgeIndexStatisticsTool,
  listCopilotComponentCollectionsTool,
  getCopilotComponentCollectionTool,
  getCopilotAgentPermissionsTool,
  getCopilotEvaluationConnectionsTool,
  getCopilotPermissionCatalogTool,
  getCopilotAgentChannelConfigurationTool,
  listCopilotAgentActivityTool,
  getCopilotAgentActivityTool,
  getCopilotAgentSessionSummaryTool,
  createCopilotKnowledgeTextFileTool,
  getCopilotStudioEvaluationDetailsTool,
  getCopilotKnowledgeFileReadinessTool,
  downloadCopilotAgentChannelManifestTool,
  createCopilotMcpConnectorTool,
  listCopilotAgentsTool,
  getCopilotAgentInventoryTool,
  ...copilotEvaluationTools,
  executeCopilotAgentTool,
  ...copilotAdminTools,
  getCopilotAgentTool,
  getCopilotAgentComponentTool,
  listCopilotAgentComponentsTool,
  createCopilotAgentTool,
  cloneCopilotAgentTool,
  addCopilotAgentToolTool,
  rebindCopilotAgentToolConnectionTool,
  configureCopilotAgentTool,
  setCopilotAgentAccessTool,
  setCopilotAgentIconTool,
  addCopilotAgentKnowledgeTool,
  addCopilotAgentTopicTool,
  addCopilotAgentEvaluationTestSetTool,
  addCopilotAgentTestCaseTool,
  getCopilotAgentTranscriptsTool,
  setCopilotAgentInstructionsTool,
  upsertCopilotAgentComponentTool,
  publishCopilotAgentTool,
  getCopilotAgentEndpointTool,
  chatWithCopilotAgentTool,
  auditCopilotAgentAccessTool,
  shareCopilotAgentTool,
  deleteCopilotAgentComponentTool,
  deleteCopilotAgentTool,
  listCopilotAgentToolsTool,
  validateCopilotAgentTool,
  executeFetchXmlTool,
  searchDataverseTool,
  getDataverseOptionSetTool,
  getDataverseRelationshipsTool,
  upsertDataverseRowTool,
  assignDataverseRowTool,
  associateDataverseRowsTool,
  disassociateDataverseRowsTool,
  createDataverseColumnTool,
  createDataverseLookupColumnTool,
  batchDataverseOperationsTool,
  // SharePoint tools
  searchSharePointSitesTool,
  getSharePointSiteTool,
  listSharePointListsTool,
  getSharePointListColumnsTool,
  listSharePointItemsTool,
  createSharePointItemTool,
  updateSharePointItemTool,
  deleteSharePointItemTool,
  // SharePoint file tools
  listSharePointFilesTool,
  uploadSharePointFileTool,
  getSharePointFileContentTool,
  // Desktop flows
  listMachinesTool,
  getMachineTool,
  listMachineGroupsTool,
  restartHostedMachineTool,
  listDesktopFlowsTool,
  getDesktopFlowTool,
  runDesktopFlowTool,
  getDesktopFlowRunsTool,
  getDesktopFlowRunTool,
  cancelDesktopFlowRunTool,
  listDesktopFlowConnectionsTool,
  getDesktopFlowRunLogsTool,
  diagnoseDesktopFlowRunTool,
  // Work queues (RPA orchestration)
  ...workQueueTools,
  // Power Pages (Dataverse config + management/hosting)
  ...powerPagesTools,
];

// Every advertised tool carries MCP annotations (title + read-only/destructive
// hints) — enforced by tests/tool-registry.test.ts and required by the
// Anthropic connectors directory.
annotateTools(tools);

const graphToolNames = new Set([
  "search_excel_files",
  "inspect_excel_file",
  "search_sharepoint_sites",
  "get_sharepoint_site",
  "list_sharepoint_lists",
  "get_sharepoint_list_columns",
  "list_sharepoint_items",
  "create_sharepoint_item",
  "update_sharepoint_item",
  "delete_sharepoint_item",
  "list_sharepoint_files",
  "upload_sharepoint_file",
  "get_sharepoint_file_content",
]);

const powerAppsToolNames = new Set([
  "list_connections",
  ...connectionLifecycleTools.map((tool) => tool.name),
  "search_connectors",
  "get_action_schema",
  "list_custom_connectors",
  "get_custom_connector",
  "create_custom_connector",
  "update_custom_connector",
  "delete_custom_connector",
  "import_openapi_connector",
  ...powerAppsTools.map((tool) => tool.name),
  ...canvasAuthoringTools.map((tool) => tool.name),
]);

const adminToolNames = new Set([...adminTools.map((tool) => tool.name)]);

const dataverseToolNames = new Set([
  ...pacCopilotTools.map((tool) => tool.name),
  ...solutionTools.map((tool) => tool.name),
  ...workQueueTools.map((tool) => tool.name),
  "list_desktop_flows",
  "get_desktop_flow",
  "run_desktop_flow",
  "get_desktop_flow_runs",
  "get_desktop_flow_run",
  "cancel_desktop_flow_run",
  "list_desktop_flow_connections",
  "get_desktop_flow_run_logs",
  "diagnose_desktop_flow_run",
  "list_machines",
  "get_machine",
  "list_machine_groups",
  "restart_hosted_machine",
  ...modelDrivenTools.map((tool) => tool.name),
  "list_solutions",
  "list_solution_flows",
  "list_canvas_apps",
  "list_ai_models",
  "list_approvals_dataverse",
  "list_dataverse_tables",
  "get_dataverse_table",
  "query_dataverse_rows",
  "get_dataverse_row",
  "create_dataverse_row",
  "update_dataverse_row",
  "delete_dataverse_row",
  "list_copilot_agents",
  "get_copilot_knowledge_index_status",
  "get_copilot_knowledge_index_statistics",
  "list_copilot_component_collections",
  "get_copilot_component_collection",
  "get_copilot_agent_permissions",
  "get_copilot_agent_evaluation_connections",
  "get_copilot_agent_evaluation_details",
  "create_copilot_mcp_connector",
  "get_copilot_permission_catalog",
  "get_copilot_agent_channel_configuration",
  "list_copilot_agent_activity",
  "get_copilot_agent_session_summary",
  "create_copilot_knowledge_text_file",
  "get_copilot_knowledge_file_readiness",
  "get_copilot_agent_activity",
  "download_copilot_agent_channel_manifest",
  "get_copilot_agent_inventory",
  ...copilotEvaluationTools.map((tool) => tool.name),
  "execute_copilot_agent",
  ...copilotAdminTools.map((tool) => tool.name),
  "get_copilot_agent",
  "get_copilot_agent_component",
  "list_copilot_agent_components",
  "create_copilot_agent",
  "clone_copilot_agent",
  "add_copilot_agent_tool",
  "rebind_copilot_agent_tool_connection",
  "configure_copilot_agent",
  "set_copilot_agent_access",
  "set_copilot_agent_icon",
  "add_copilot_agent_knowledge",
  "add_copilot_agent_topic",
  "add_copilot_agent_test_case",
  "get_copilot_agent_transcripts",
  "set_copilot_agent_instructions",
  "upsert_copilot_agent_component",
  "publish_copilot_agent",
  "get_copilot_agent_endpoint",
  "chat_with_copilot_agent",
  "audit_copilot_agent_access",
  "share_copilot_agent",
  "delete_copilot_agent_component",
  "delete_copilot_agent",
  "list_copilot_agent_tools",
  "validate_copilot_agent",
  "execute_fetchxml",
  "search_dataverse",
  "get_dataverse_option_set",
  "get_dataverse_relationships",
  "upsert_dataverse_row",
  "assign_dataverse_row",
  "associate_dataverse_rows",
  "disassociate_dataverse_rows",
  "create_dataverse_column",
  "create_dataverse_lookup_column",
  "batch_dataverse_operations",
]);

const powerPagesConfigToolNames = new Set([
  "list_powerpages_sites",
  "get_powerpages_site",
  "list_powerpages_components",
  "get_powerpages_component",
  "create_powerpages_component",
  "update_powerpages_component",
  "delete_powerpages_component",
  "manage_powerpages_relationship",
  "upload_powerpages_webfile_content",
  ...pacPagesTools.map((tool) => tool.name),
]);

const powerPagesManagementToolNames = new Set([
  "list_powerpages_websites",
  "get_powerpages_website",
  "create_powerpages_website",
  "delete_powerpages_website",
  "restart_powerpages_website",
  ...additionalPowerPagesManagementTools.map((tool) => tool.name),
]);

export function getRequiredFeatureForTool(toolName: string): FeatureId | null {
  if (graphToolNames.has(toolName)) return "graph";
  if (powerAppsToolNames.has(toolName)) return "powerApps";
  if (adminToolNames.has(toolName)) return "admin";
  if (dataverseToolNames.has(toolName)) return "dataverse";
  if (powerPagesConfigToolNames.has(toolName)) return "powerPages";
  if (powerPagesManagementToolNames.has(toolName)) return "powerPagesManagement";
  return null;
}

export function isToolEnabledForFeatures(
  toolName: string,
  features: readonly FeatureId[] | undefined,
): boolean {
  const requiredFeature = getRequiredFeatureForTool(toolName);
  if (!requiredFeature) return true;
  return normalizeFeatures(features).includes(requiredFeature);
}

export function getToolsForFeatures(features: readonly FeatureId[] | undefined): Tool[] {
  return tools.filter((tool) => isToolEnabledForFeatures(tool.name, features));
}

/**
 * Tool execution context
 */
export interface ToolContext {
  flowApi: FlowManagementApi;
  connectorApi: ConnectorMetadataApi | null;
  graphApi: GraphApi | null;
  powerAppsApi: PowerAppsApi | null;
  canvasAuthoringClient?: CanvasAuthoringClientLike | null;
  adminApi: PowerPlatformAdminApi | null;
  powerPlatformInventoryApi?: PowerPlatformInventoryApi | null;
  copilotEvaluationApi?: CopilotEvaluationApi | null;
  copilotStudioExecutionApi?: CopilotStudioExecutionApi | null;
  copilotAdminApi?: CopilotAdminApi | null;
  dataverseApi: DataverseApi | null;
  modelDrivenApi?: ModelDrivenApi | null;
  desktopFlowsApi?: DesktopFlowsApi | null;
  connectionsApi?: ConnectionsApi | null;
  powerPagesApi: PowerPagesApi | null;
  cache: SchemaCache | null;
  defaultEnvironmentId: string;
  enabledFeatures?: FeatureId[];
  /** App identity for the in-chat sign_in tool. */
  auth: Parameters<typeof handleSignIn>[0];
  /** Shared auth provider — lets switch_environment rebuild env-bound clients. */
  authProvider?: AuthProvider;
  /** Session-scoped environment state (switch_environment). */
  environmentSession?: EnvironmentSession;
}

/**
 * Rich tool result: prose for the model plus an optional machine-parseable mirror
 * emitted as MCP `structuredContent` (#11). Handlers that don't need it keep
 * returning a plain string.
 */
export interface ToolRichResult {
  text: string;
  structured?: Record<string, unknown>;
  /** A failed or uncertain operation may retain structured inspection data. */
  isError?: boolean;
}
type ToolHandlerResult = string | ToolRichResult;

/**
 * A tool handler bound to the execution context. Returns the tool's text result.
 */
type ToolRun = (context: ToolContext, input: unknown) => Promise<ToolHandlerResult>;

/** File operations use only the selected provider; never the CLI Activity adapter. */
function copilotKnowledgeFileConfig(c: ToolContext) {
  const auth = c.authProvider,
    session = c.environmentSession,
    api = c.dataverseApi;
  const environment = c.defaultEnvironmentId;
  const key =
    typeof environment === "string" ? environment.toLowerCase().replace(/^default-/, "") : "";
  const explicit = session?.configOverrides?.[key]?.dataverseUrl;
  const normalize = (url: unknown) =>
    typeof url === "string" &&
    /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:api\.)?crm[0-9]{0,2}\.dynamics\.com\/?$(?![\s\S])/i.test(
      url,
    )
      ? url.toLowerCase().replace(/\/$/, "")
      : null;
  const origin = normalize(explicit);
  const check = () => {
    if (
      !auth ||
      !api ||
      !session ||
      !origin ||
      !/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$(?![\s\S])/i.test(key) ||
      c.authProvider !== auth ||
      c.dataverseApi !== api ||
      c.environmentSession !== session ||
      c.defaultEnvironmentId !== environment ||
      typeof session.currentId !== "string" ||
      session.currentId.toLowerCase() !== environment.toLowerCase() ||
      normalize(session.dataverseUrl) !== origin ||
      session.configOverrides?.[key]?.dataverseUrl !== explicit
    )
      throw new CopilotKnowledgeFileError("configuration");
  };
  check();
  return {
    environmentUrl: origin!,
    authProvider: {
      getCurrentAccount: () => {
        check();
        return auth!.getCurrentAccount();
      },
      tryGetTokenForResourceSilent: async (resource: string) => {
        check();
        if (!auth!.tryGetTokenForResourceSilent) return null;
        const token = await auth!.tryGetTokenForResourceSilent(resource);
        check();
        return token;
      },
    },
  };
}

function copilotKnowledgeFileContext(c: ToolContext) {
  return new CopilotKnowledgeFileApi(copilotKnowledgeFileConfig(c));
}

// Guard/arg-binding factories — each encodes one "requires + how the handler is called"
// pattern once, so the handler map below stays one line per tool. The generics make
// TypeScript enforce that a required API guard is present: handlers take a non-null api,
// so omitting a guard would pass `Api | null` and fail typecheck.
const SHAREPOINT_GRAPH_MSG =
  "Graph API not configured — SharePoint tools require Microsoft Graph access";
const flowRun =
  (h: (api: FlowManagementApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) =>
    h(c.flowApi, i);
const connectorRun =
  (h: (api: ConnectorMetadataApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return h(c.connectorApi, i);
  };
const graphRun =
  (
    h: (api: GraphApi, input: unknown) => Promise<ToolHandlerResult>,
    msg = "Graph API not configured",
  ): ToolRun =>
  (c, i) => {
    if (!c.graphApi) throw new ValidationError(msg);
    return h(c.graphApi, i);
  };
const powerAppsRun =
  (h: (api: PowerAppsApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.powerAppsApi) throw new ValidationError("Power Apps API not configured");
    return h(c.powerAppsApi, i);
  };
const canvasAuthoringRuns: Record<string, ToolRun> = Object.fromEntries(
  Object.entries(canvasAuthoringHandlers).map(([name, handler]) => [
    name,
    (context: ToolContext, input: unknown) =>
      handler({ client: context.canvasAuthoringClient ?? null }, input),
  ]),
);
const adminRun =
  (h: (api: PowerPlatformAdminApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.adminApi) throw new ValidationError("Admin API not configured");
    return h(c.adminApi, i);
  };
const powerPlatformInventoryRun =
  (h: (api: PowerPlatformInventoryApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.powerPlatformInventoryApi) {
      throw new ValidationError(
        "Power Platform inventory API not configured — enable the Dataverse/Copilot Studio tool surface and re-run setup",
      );
    }
    return h(c.powerPlatformInventoryApi, i);
  };
const copilotEvaluationRun =
  (h: (api: CopilotEvaluationApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.copilotEvaluationApi) {
      throw new ValidationError(
        "Copilot Studio evaluation API not configured — enable the Dataverse/Copilot Studio tool surface",
      );
    }
    return h(c.copilotEvaluationApi, i);
  };
const copilotExecutionRun =
  (h: (api: CopilotStudioExecutionApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.copilotStudioExecutionApi) {
      throw new ValidationError(
        "Authenticated Copilot Studio execution is not configured — enable the Dataverse/Copilot Studio tool surface",
      );
    }
    return h(c.copilotStudioExecutionApi, i);
  };
const copilotAdminRun =
  (h: (api: CopilotAdminApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.copilotAdminApi) {
      throw new ValidationError(
        "Copilot Studio governance API not configured — enable the Dataverse/Copilot Studio tool surface",
      );
    }
    return h(c.copilotAdminApi, i);
  };
const dataverseRun =
  (h: (arg: { api: DataverseApi | null }, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) =>
    h({ api: c.dataverseApi }, i);
const modelDrivenRun =
  (h: (api: ModelDrivenApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.modelDrivenApi) throw new ValidationError("Model-driven Apps API not configured");
    return h(c.modelDrivenApi, i);
  };
const connectionsRun =
  (h: (api: ConnectionsApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.connectionsApi) {
      throw new ValidationError("Connections API not configured for this environment.");
    }
    return h(c.connectionsApi, i);
  };
const desktopFlowsRun =
  (h: (api: DesktopFlowsApi, input: unknown) => Promise<ToolHandlerResult>): ToolRun =>
  (c, i) => {
    if (!c.desktopFlowsApi) {
      throw new ValidationError(
        "Desktop Flows API not configured — this environment has no reachable Dataverse organization (desktop flows live in Dataverse). Check that the environment has a Dataverse database, or set dataverseUrl in your environment config.",
      );
    }
    return h(c.desktopFlowsApi, i);
  };
const powerPagesManagementRun =
  (
    h: (
      context: { api: PowerPagesApi | null; adminApi: PowerPlatformAdminApi | null },
      input: unknown,
    ) => Promise<ToolHandlerResult>,
  ): ToolRun =>
  (c, i) =>
    h({ api: c.powerPagesApi, adminApi: c.adminApi }, i);

const additionalPowerPagesRuns: Record<string, ToolRun> = Object.fromEntries(
  [...Object.entries(powerPagesManagementHandlers), ...Object.entries(pacPagesHandlers)].map(
    ([name, handler]) => [name, powerPagesManagementRun(handler)],
  ),
);

/**
 * Single source of truth for tool dispatch: tool name → handler. Replaces the former
 * 121-case switch, so a tool can't be advertised (in `tools`) without a handler or
 * vice-versa (enforced by tests/tool-registry.test.ts). Handlers themselves are
 * unchanged — these entries only bind them to the execution context.
 */
export const toolHandlers: Record<string, ToolRun> = {
  ...pacCopilotHandlers,
  // Authentication
  sign_in: (c, i) => handleSignIn(c.auth, i),
  // Phase 1
  list_flows: flowRun(handleListFlows),
  get_flow: flowRun(handleGetFlow),
  get_runs: flowRun(handleGetRuns),
  toggle_flow: flowRun(handleToggleFlow),
  // Phase 2
  search_connectors: (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return handleSearchConnectors({ cache: c.cache, api: c.connectorApi }, i);
  },
  get_action_schema: (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return handleGetActionSchema({ cache: c.cache, api: c.connectorApi }, i);
  },
  // Phase 3
  create_flow: flowRun(handleCreateFlow),
  update_flow: flowRun(handleUpdateFlow),
  preview_update: flowRun(handlePreviewUpdate),
  ...Object.fromEntries(
    Object.entries(flowHistoryHandlers).map(([name, handler]) => [name, flowRun(handler)]),
  ),
  cancel_all_runs: flowRun(handleCancelAllRuns),
  delete_flow: flowRun(handleDeleteFlow),
  validate_flow: flowRun(handleValidateFlow),
  clone_flow: flowRun(handleCloneFlow),
  run_flow: flowRun(handleRunFlow),
  // Phase 4
  list_connections: (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return handleListConnections({ api: c.connectorApi }, i);
  },
  get_run_actions: flowRun(handleGetRunActions),
  get_run_action_repetitions: flowRun(handleGetRunActionRepetitions),
  get_flow_permissions: flowRun(handleGetFlowPermissions),
  share_flow: flowRun(handleShareFlow),
  // Phase 5
  resubmit_run: flowRun(handleResubmitRun),
  cancel_run: flowRun(handleCancelRun),
  list_environments: flowRun(handleListEnvironments),
  switch_environment: (c, i) => handleSwitchEnvironment(c, i),
  // Phase 6
  list_flow_versions: flowRun(handleListFlowVersions),
  export_flow: (c, i) => handleExportFlow({ api: c.flowApi, adminApi: c.adminApi }, i),
  list_approvals: flowRun(handleListApprovals),
  respond_approval: flowRun(handleRespondApproval),
  // Interactive planning
  plan_flow: (c, i) => handlePlanFlow({ api: c.connectorApi }, i),
  diagnose_flow: flowRun(handleDiagnoseFlow),
  build_flow: (c, i) => handleBuildFlow({ flowApi: c.flowApi, connectorApi: c.connectorApi }, i),
  // Custom connectors
  list_custom_connectors: connectorRun(handleListCustomConnectors),
  diagnose_connector_auth: (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return handleDiagnoseConnectorAuth({ api: c.connectorApi }, i);
  },
  update_connector_oauth_scopes: (c, i) => {
    if (!c.connectorApi) throw new ValidationError("Connector API not configured");
    return handleUpdateConnectorOauthScopes(c.connectorApi, i);
  },
  get_custom_connector: connectorRun(handleGetCustomConnector),
  create_custom_connector: connectorRun(handleCreateCustomConnector),
  update_custom_connector: connectorRun(handleUpdateCustomConnector),
  delete_custom_connector: connectorRun(handleDeleteCustomConnector),
  import_openapi_connector: connectorRun(handleImportOpenApi),
  plan_custom_connector: (_c, i) => handlePlanCustomConnector(null, i),
  // Excel
  search_excel_files: graphRun(handleSearchExcelFiles),
  inspect_excel_file: graphRun(handleInspectExcelFile),
  // Power Apps
  list_powerapps: powerAppsRun(handleListPowerApps),
  get_powerapp: powerAppsRun(handleGetPowerApp),
  delete_powerapp: powerAppsRun(handleDeletePowerApp),
  publish_powerapp: powerAppsRun(handlePublishPowerApp),
  get_powerapp_versions: powerAppsRun(handleGetPowerAppVersions),
  restore_powerapp_version: powerAppsRun(handleRestorePowerAppVersion),
  set_powerapp_display_name: powerAppsRun(handleSetPowerAppDisplayName),
  get_powerapp_permissions: powerAppsRun(handleGetPowerAppPermissions),
  share_powerapp: powerAppsRun(handleSharePowerApp),
  unshare_powerapp: powerAppsRun(handleUnsharePowerApp),
  list_powerapps_admin: powerAppsRun(handleListPowerAppsAdmin),
  get_powerapp_admin: powerAppsRun(handleGetPowerAppAdmin),
  delete_powerapp_admin: powerAppsRun(handleDeletePowerAppAdmin),
  set_powerapp_owner: powerAppsRun(handleSetPowerAppOwner),
  quarantine_powerapp: powerAppsRun(handleQuarantinePowerApp),
  // Canvas Apps source authoring (preview)
  ...canvasAuthoringRuns,
  // Model-driven Power Apps
  list_model_driven_apps: modelDrivenRun(handleListModelDrivenApps),
  get_model_driven_app: modelDrivenRun(handleGetModelDrivenApp),
  create_model_driven_app: modelDrivenRun(handleCreateModelDrivenApp),
  update_model_driven_app: modelDrivenRun(handleUpdateModelDrivenApp),
  delete_model_driven_app: modelDrivenRun(handleDeleteModelDrivenApp),
  get_model_driven_app_components: modelDrivenRun(handleGetModelDrivenAppComponents),
  add_model_driven_app_components: modelDrivenRun(handleAddModelDrivenAppComponents),
  remove_model_driven_app_components: modelDrivenRun(handleRemoveModelDrivenAppComponents),
  validate_model_driven_app: modelDrivenRun(handleValidateModelDrivenApp),
  publish_model_driven_app: modelDrivenRun(handlePublishModelDrivenApp),
  list_model_driven_app_roles: modelDrivenRun(handleListModelDrivenAppRoles),
  grant_model_driven_app_role: modelDrivenRun(handleGrantModelDrivenAppRole),
  revoke_model_driven_app_role: modelDrivenRun(handleRevokeModelDrivenAppRole),
  // Admin — environment lifecycle
  get_environment: adminRun(handleGetEnvironment),
  create_environment: adminRun(handleCreateEnvironment),
  delete_environment: adminRun(handleDeleteEnvironment),
  backup_environment: adminRun(handleBackupEnvironment),
  restore_environment: adminRun(handleRestoreEnvironment),
  copy_environment: adminRun(handleCopyEnvironment),
  reset_environment: adminRun(handleResetEnvironment),
  list_environment_backups: adminRun(handleListEnvironmentBackups),
  // Admin — DLP
  list_dlp_policies: adminRun(handleListDlpPolicies),
  get_dlp_policy: adminRun(handleGetDlpPolicy),
  create_dlp_policy: adminRun(handleCreateDlpPolicy),
  update_dlp_policy: adminRun(handleUpdateDlpPolicy),
  delete_dlp_policy: adminRun(handleDeleteDlpPolicy),
  get_dlp_connector_configs: adminRun(handleGetDlpConnectorConfigs),
  // Admin — managed environments
  get_managed_environment_settings: adminRun(handleGetManagedEnvironmentSettings),
  enable_managed_environment: adminRun(handleEnableManagedEnvironment),
  disable_managed_environment: adminRun(handleDisableManagedEnvironment),
  update_managed_environment_settings: adminRun(handleUpdateManagedEnvironmentSettings),
  // Admin — capacity & licensing
  get_tenant_capacity: adminRun(handleGetTenantCapacity),
  get_environment_capacity: adminRun(handleGetEnvironmentCapacity),
  list_billing_policies: adminRun(handleListBillingPolicies),
  get_billing_policy: adminRun(handleGetBillingPolicy),
  get_api_request_summary: adminRun(handleGetApiRequestSummary),
  // Solution ALM
  export_solution: dataverseRun(handleExportSolution),
  import_solution: dataverseRun(handleImportSolution),
  publish_all_customizations: dataverseRun(handlePublishAllCustomizations),
  create_solution: dataverseRun(handleCreateSolution),
  delete_solution: dataverseRun(handleDeleteSolution),
  add_solution_component: dataverseRun(handleAddSolutionComponent),
  remove_solution_component: dataverseRun(handleRemoveSolutionComponent),
  clone_solution: dataverseRun(handleCloneSolution),
  // Test flow
  test_flow: flowRun(handleTestFlow),
  // Expression help
  get_expression_help: (_c, i) => handleExpressionHelp(i),
  // Dataverse
  list_solutions: dataverseRun(handleListSolutions),
  list_solution_flows: dataverseRun(handleListSolutionFlows),
  list_canvas_apps: dataverseRun(handleListCanvasApps),
  list_ai_models: dataverseRun(handleListAIModels),
  list_approvals_dataverse: dataverseRun(handleListApprovalsDataverse),
  list_dataverse_tables: dataverseRun(handleListDataverseTables),
  get_dataverse_table: dataverseRun(handleGetDataverseTable),
  query_dataverse_rows: dataverseRun(handleQueryDataverseRows),
  get_dataverse_row: dataverseRun(handleGetDataverseRow),
  create_dataverse_row: dataverseRun(handleCreateDataverseRow),
  update_dataverse_row: dataverseRun(handleUpdateDataverseRow),
  delete_dataverse_row: dataverseRun(handleDeleteDataverseRow),
  list_copilot_agents: dataverseRun(handleListCopilotAgents),
  get_copilot_agent_inventory: powerPlatformInventoryRun(handleGetCopilotAgentInventory),
  list_copilot_agent_evaluation_test_sets: copilotEvaluationRun(
    handleListCopilotEvaluationTestSets,
  ),
  get_copilot_agent_evaluation_test_set: copilotEvaluationRun(handleGetCopilotEvaluationTestSet),
  start_copilot_agent_evaluation: copilotEvaluationRun(handleStartCopilotEvaluation),
  list_copilot_agent_evaluation_runs: copilotEvaluationRun(handleListCopilotEvaluationRuns),
  get_copilot_agent_evaluation_run: copilotEvaluationRun(handleGetCopilotEvaluationRun),
  download_copilot_agent_evaluation_snapshot: copilotEvaluationRun(
    handleDownloadCopilotEvaluationSnapshot,
  ),
  execute_copilot_agent: copilotExecutionRun(handleExecuteCopilotAgent),
  get_copilot_agent_quarantine: copilotAdminRun(handleGetCopilotAgentQuarantine),
  set_copilot_agent_quarantine: copilotAdminRun(handleSetCopilotAgentQuarantine),
  get_copilot_agent_connector_consent_bypass: copilotAdminRun(
    handleGetCopilotAgentConnectorConsentBypass,
  ),
  set_copilot_agent_connector_consent_bypass: copilotAdminRun(
    handleSetCopilotAgentConnectorConsentBypass,
  ),
  reassign_copilot_agent_owner: copilotAdminRun(handleReassignCopilotAgentOwner),
  get_copilot_agent: dataverseRun(handleGetCopilotAgent),
  get_copilot_agent_component: dataverseRun(handleGetCopilotAgentComponent),
  list_copilot_agent_components: dataverseRun(handleListCopilotAgentComponents),
  create_copilot_agent: dataverseRun(handleCreateCopilotAgent),
  clone_copilot_agent: dataverseRun(handleCloneCopilotAgent),
  add_copilot_agent_tool: dataverseRun(handleAddCopilotAgentTool),
  rebind_copilot_agent_tool_connection: (c, i) =>
    handleRebindCopilotAgentToolConnection(
      { api: c.dataverseApi, connectorApi: c.connectorApi, environmentId: c.defaultEnvironmentId },
      i,
    ),
  configure_copilot_agent: dataverseRun(handleConfigureCopilotAgent),
  list_copilot_component_collections: dataverseRun(handleListCopilotComponentCollections),
  get_copilot_knowledge_index_status: dataverseRun(handleGetCopilotKnowledgeIndexStatus),
  get_copilot_knowledge_index_statistics: dataverseRun(handleGetCopilotKnowledgeIndexStatistics),
  get_copilot_component_collection: dataverseRun(handleGetCopilotComponentCollection),
  get_copilot_agent_permissions: dataverseRun(handleGetCopilotAgentPermissions),
  get_copilot_agent_channel_configuration: dataverseRun(handleGetCopilotAgentChannelConfiguration),
  list_copilot_agent_activity: (c, i) =>
    handleListCopilotAgentActivity(
      {
        activityApi:
          c.authProvider && c.dataverseApi
            ? new CopilotActivityApi(c.authProvider, c.dataverseApi, () => c.defaultEnvironmentId)
            : null,
      },
      i,
    ),
  get_copilot_agent_session_summary: (c, i) =>
    handleGetCopilotAgentSessionSummary(
      {
        monitorApi:
          c.authProvider && c.dataverseApi
            ? new CopilotMonitorApi(c.authProvider, c.dataverseApi, () => c.defaultEnvironmentId)
            : null,
      },
      i,
    ),
  get_copilot_agent_activity: (c, i) =>
    handleGetCopilotAgentActivity(
      {
        activityDetailApi:
          c.authProvider && c.dataverseApi
            ? new CopilotActivityDetailApi(
                c.authProvider,
                c.dataverseApi,
                new CopilotActivityApi(
                  c.authProvider,
                  c.dataverseApi,
                  () => c.defaultEnvironmentId,
                ),
                () => c.defaultEnvironmentId,
              )
            : null,
      },
      i,
    ),
  get_copilot_agent_evaluation_connections: (c, i) => handleGetCopilotEvaluationConnections(c, i),
  create_copilot_knowledge_text_file: (c, i) => {
    const parsed = createCopilotKnowledgeTextFileInputSchema.safeParse(i);
    if (!parsed.success) throw new CopilotKnowledgeFileError("input");
    return handleCreateCopilotKnowledgeTextFile(
      { knowledgeFileApi: copilotKnowledgeFileContext(c) },
      parsed.data,
    );
  },
  get_copilot_knowledge_file_readiness: (c, i) => {
    const parsed = getCopilotKnowledgeFileReadinessInputSchema.safeParse(i);
    if (!parsed.success) throw new CopilotKnowledgeFileReadinessError("input");
    return handleGetCopilotKnowledgeFileReadiness(
      { knowledgeFileReadinessApi: new CopilotKnowledgeFileReadinessApi(copilotKnowledgeFileConfig(c)) },
      parsed.data,
    );
  },
  create_copilot_mcp_connector: (c, i) =>
    handleCreateCopilotMcpConnector(
      {
        connectorApi: c.authProvider
          ? new CopilotMcpConnectorApi(
              c.authProvider,
              new EnvironmentApi({ authProvider: c.authProvider }),
            )
          : null,
      },
      i,
    ),
  get_copilot_permission_catalog: (c, i) =>
    handleGetCopilotPermissionCatalog(
      {
        permissionCatalogApi: c.authProvider
          ? new CopilotPermissionCatalogApi(c.authProvider)
          : null,
      },
      i,
    ),
  get_copilot_agent_evaluation_details: (c, i) =>
    handleGetCopilotStudioEvaluationDetails(
      {
        detailsApi: c.authProvider
          ? new CopilotStudioEvaluationDetailsApi(
              c.authProvider,
              new EnvironmentApi({ authProvider: c.authProvider }),
              c.defaultEnvironmentId,
            )
          : null,
      },
      i,
    ),
  download_copilot_agent_channel_manifest: (c, i) => {
    if (!c.authProvider)
      throw new ValidationError("Power Platform authentication is not configured");
    return handleDownloadCopilotAgentChannelManifest(
      new CopilotChannelApi({
        authProvider: c.authProvider,
        defaultEnvironmentId: c.defaultEnvironmentId,
      }),
      i,
    );
  },
  set_copilot_agent_access: dataverseRun(handleSetCopilotAgentAccess),
  set_copilot_agent_icon: dataverseRun(handleSetCopilotAgentIcon),
  add_copilot_agent_knowledge: dataverseRun(handleAddCopilotAgentKnowledge),
  add_copilot_agent_topic: dataverseRun(handleAddCopilotAgentTopic),
  add_copilot_agent_test_case: dataverseRun(handleAddCopilotAgentTestCase),
  add_copilot_agent_evaluation_test_set: dataverseRun(handleAddCopilotAgentEvaluationTestSet),
  get_copilot_agent_transcripts: dataverseRun(handleGetCopilotAgentTranscripts),
  set_copilot_agent_instructions: dataverseRun(handleSetCopilotAgentInstructions),
  upsert_copilot_agent_component: dataverseRun(handleUpsertCopilotAgentComponent),
  publish_copilot_agent: dataverseRun(handlePublishCopilotAgent),
  get_copilot_agent_endpoint: dataverseRun(handleGetCopilotAgentEndpoint),
  chat_with_copilot_agent: dataverseRun(handleChatWithCopilotAgent),
  audit_copilot_agent_access: dataverseRun(handleAuditCopilotAgentAccess),
  share_copilot_agent: dataverseRun(handleShareCopilotAgent),
  delete_copilot_agent_component: dataverseRun(handleDeleteCopilotAgentComponent),
  delete_copilot_agent: dataverseRun(handleDeleteCopilotAgent),
  validate_copilot_agent: (c, i) =>
    handleValidateCopilotAgent({ api: c.dataverseApi, connectorApi: c.connectorApi }, i),
  list_copilot_agent_tools: (c, i) =>
    handleListCopilotAgentTools({ api: c.dataverseApi, connectorApi: c.connectorApi }, i),
  execute_fetchxml: dataverseRun(handleExecuteFetchXml),
  search_dataverse: dataverseRun(handleSearchDataverse),
  get_dataverse_option_set: dataverseRun(handleGetDataverseOptionSet),
  get_dataverse_relationships: dataverseRun(handleGetDataverseRelationships),
  upsert_dataverse_row: dataverseRun(handleUpsertDataverseRow),
  assign_dataverse_row: dataverseRun(handleAssignDataverseRow),
  associate_dataverse_rows: dataverseRun(handleAssociateDataverseRows),
  disassociate_dataverse_rows: dataverseRun(handleDisassociateDataverseRows),
  create_dataverse_column: dataverseRun(handleCreateDataverseColumn),
  create_dataverse_lookup_column: dataverseRun(handleCreateDataverseLookupColumn),
  batch_dataverse_operations: dataverseRun(handleBatchDataverseOperations),
  // Power Pages — Dataverse config
  list_powerpages_sites: dataverseRun(handleListPowerPagesSites),
  get_powerpages_site: dataverseRun(handleGetPowerPagesSite),
  list_powerpages_components: dataverseRun(handleListPowerPagesComponents),
  get_powerpages_component: dataverseRun(handleGetPowerPagesComponent),
  create_powerpages_component: dataverseRun(handleCreatePowerPagesComponent),
  update_powerpages_component: dataverseRun(handleUpdatePowerPagesComponent),
  delete_powerpages_component: dataverseRun(handleDeletePowerPagesComponent),
  manage_powerpages_relationship: dataverseRun(handleManagePowerPagesRelationship),
  upload_powerpages_webfile_content: dataverseRun(handleUploadPowerPagesWebFileContent),
  // Power Pages — management/hosting
  list_powerpages_websites: (c, i) =>
    handleListPowerPagesWebsites({ api: c.powerPagesApi, adminApi: c.adminApi }, i),
  get_powerpages_website: (c, i) =>
    handleGetPowerPagesWebsite({ api: c.powerPagesApi, adminApi: c.adminApi }, i),
  create_powerpages_website: (c, i) =>
    handleCreatePowerPagesWebsite({ api: c.powerPagesApi, adminApi: c.adminApi }, i),
  delete_powerpages_website: (c, i) =>
    handleDeletePowerPagesWebsite({ api: c.powerPagesApi, adminApi: c.adminApi }, i),
  restart_powerpages_website: (c, i) =>
    handleRestartPowerPagesWebsite({ api: c.powerPagesApi, adminApi: c.adminApi }, i),
  ...additionalPowerPagesRuns,
  // SharePoint
  search_sharepoint_sites: graphRun(handleSearchSharePointSites, SHAREPOINT_GRAPH_MSG),
  get_sharepoint_site: graphRun(handleGetSharePointSite, SHAREPOINT_GRAPH_MSG),
  list_sharepoint_lists: graphRun(handleListSharePointLists, SHAREPOINT_GRAPH_MSG),
  get_sharepoint_list_columns: graphRun(handleGetSharePointListColumns, SHAREPOINT_GRAPH_MSG),
  list_sharepoint_items: graphRun(handleListSharePointItems, SHAREPOINT_GRAPH_MSG),
  create_sharepoint_item: graphRun(handleCreateSharePointItem, SHAREPOINT_GRAPH_MSG),
  update_sharepoint_item: graphRun(handleUpdateSharePointItem, SHAREPOINT_GRAPH_MSG),
  delete_sharepoint_item: graphRun(handleDeleteSharePointItem, SHAREPOINT_GRAPH_MSG),
  list_sharepoint_files: graphRun(handleListSharePointFiles, SHAREPOINT_GRAPH_MSG),
  upload_sharepoint_file: graphRun(handleUploadSharePointFile, SHAREPOINT_GRAPH_MSG),
  get_sharepoint_file_content: graphRun(handleGetSharePointFileContent, SHAREPOINT_GRAPH_MSG),
  // Desktop flows
  list_machines: desktopFlowsRun(handleListMachines),
  get_machine: desktopFlowsRun(handleGetMachine),
  list_machine_groups: desktopFlowsRun(handleListMachineGroups),
  restart_hosted_machine: desktopFlowsRun(handleRestartHostedMachine),
  list_desktop_flows: desktopFlowsRun(handleListDesktopFlows),
  get_desktop_flow: desktopFlowsRun(handleGetDesktopFlow),
  run_desktop_flow: desktopFlowsRun(handleRunDesktopFlow),
  get_desktop_flow_runs: desktopFlowsRun(handleGetDesktopFlowRuns),
  get_desktop_flow_run: desktopFlowsRun(handleGetDesktopFlowRun),
  cancel_desktop_flow_run: desktopFlowsRun(handleCancelDesktopFlowRun),
  list_desktop_flow_connections: desktopFlowsRun(handleListDesktopFlowConnections),
  get_desktop_flow_run_logs: desktopFlowsRun(handleGetDesktopFlowRunLogs),
  diagnose_desktop_flow_run: desktopFlowsRun(handleDiagnoseDesktopFlowRun),
  // Connection lifecycle
  ...Object.fromEntries(
    Object.entries(connectionLifecycleHandlers).map(([name, handler]) => [
      name,
      connectionsRun(handler),
    ]),
  ),
  // Work queues
  ...Object.fromEntries(
    Object.entries(workQueueHandlers).map(([name, handler]) => [name, desktopFlowsRun(handler)]),
  ),
};

/**
 * Reject arguments the tool does not declare.
 *
 * Every handler validates with a plain `z.object()`, and zod STRIPS unknown keys rather
 * than complaining — so a misremembered parameter name was silently discarded and the
 * call reported success having done nothing. Field report: `update_flow` was called with
 * `patchTrigger` (the real name is `trigger`); the tool answered "Updated ..." and the
 * flow's trigger was untouched. A no-op that reports success is worse than an error,
 * because the caller moves on believing the change landed.
 *
 * Checked against the published JSON Schema — the same contract the model is given — so
 * it stays correct as tools change, with no per-tool upkeep. Only TOP-LEVEL keys are
 * checked: nested objects (`fields`, `data`, a flow's `trigger`/`actions`) are
 * deliberately open-ended and several declare `additionalProperties: true`.
 */
/**
 * Rename `environment` to `environmentId` for tools that declare only the
 * latter (environment lifecycle admin tools), so callers can use the same
 * parameter name across all 252 tools. Anything else passes through untouched.
 */
export function aliasEnvironmentParameter(toolName: string, input: unknown): unknown {
  if (!input || typeof input !== "object" || Array.isArray(input)) return input;
  const record = input as Record<string, unknown>;
  if (!("environment" in record) || "environmentId" in record) return input;
  const schema = tools.find((tool) => tool.name === toolName)?.inputSchema as
    { properties?: Record<string, unknown> } | undefined;
  const props = schema?.properties;
  if (!props || !("environmentId" in props) || "environment" in props) return input;
  const { environment, ...rest } = record;
  return { ...rest, environmentId: environment };
}

function assertNoUnknownParameters(toolName: string, input: unknown): void {
  if (!input || typeof input !== "object" || Array.isArray(input)) return;

  const schema = tools.find((tool) => tool.name === toolName)?.inputSchema as
    { properties?: Record<string, unknown>; additionalProperties?: unknown } | undefined;
  // No declared properties, or the tool opts into extras: nothing to enforce.
  if (!schema?.properties || schema.additionalProperties === true) return;

  const declared = Object.keys(schema.properties);
  const unknown = Object.keys(input as Record<string, unknown>).filter(
    (key) => !Object.prototype.hasOwnProperty.call(schema.properties, key),
  );
  if (unknown.length === 0) return;

  // Near-misses are the common case (patchTrigger/trigger), so name the likely intent
  // instead of only listing every valid parameter.
  const details = unknown.map((key) => {
    const suggestion = closestParameterName(key, declared);
    return suggestion ? `'${key}' (did you mean '${suggestion}'?)` : `'${key}'`;
  });
  // Dataverse-backed tools have no environment parameter on purpose: they act
  // on the session's current environment. Say how to change it rather than
  // leaving "does not accept 'environment'" as a dead end.
  const environmentHint =
    unknown.some((key) => key === "environment" || key === "environmentId") &&
    !declared.includes("environment") &&
    !declared.includes("environmentId")
      ? " This tool works in the session's current environment — call switch_environment (by name or id) to change it, then call this tool again without the environment parameter."
      : "";
  throw new ValidationError(
    `${toolName} does not accept ${details.join(", ")}. ` +
      `It would have been ignored and the call would have reported success without doing that part. ` +
      `Valid parameters: ${declared.join(", ")}.` +
      environmentHint,
  );
}

/**
 * Closest declared name, ignoring case.
 *
 * Containment is checked before edit distance because the mistakes that actually happen
 * are a decorated version of the right name — `patchTrigger` for `trigger`, borrowed
 * from the sibling `patchActions`. Edit distance alone scores that 5 and misses it,
 * which is precisely the case this exists for.
 */
function closestParameterName(key: string, declared: readonly string[]): string | undefined {
  const lowerKey = key.toLowerCase();

  // Longest containment match wins — the most specific one is the likeliest intent.
  let contained: string | undefined;
  for (const candidate of declared) {
    const lowerCandidate = candidate.toLowerCase();
    if (lowerKey.includes(lowerCandidate) || lowerCandidate.includes(lowerKey)) {
      if (!contained || candidate.length > contained.length) contained = candidate;
    }
  }
  if (contained) return contained;

  let best: string | undefined;
  let bestDistance = Infinity;
  for (const candidate of declared) {
    const distance = editDistance(lowerKey, candidate.toLowerCase());
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  // Allow more slack on longer names, but never so much that everything "matches".
  const tolerance = Math.min(4, Math.floor(key.length / 2) + 1);
  return bestDistance <= tolerance ? best : undefined;
}

// Levenshtein, two rows. Every index below is in range by construction; the `?? 0`
// fallbacks satisfy noUncheckedIndexedAccess without widening the arithmetic.
function editDistance(a: string, b: string): number {
  let previous: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current: number[] = [i];
    for (let j = 1; j <= b.length; j++) {
      const deletion = (previous[j] ?? 0) + 1;
      const insertion = (current[j - 1] ?? 0) + 1;
      const substitution = (previous[j - 1] ?? 0) + (a[i - 1] === b[j - 1] ? 0 : 1);
      current[j] = Math.min(deletion, insertion, substitution);
    }
    previous = current;
  }
  return previous[b.length] ?? 0;
}

/**
 * Execute a tool by name
 */
export async function executeTool(
  context: ToolContext,
  toolName: string,
  input: unknown,
): Promise<{
  content: Array<{ type: "text"; text: string }>;
  structuredContent?: Record<string, unknown>;
  isError?: boolean;
}> {
  try {
    logger.info({ toolName }, "Executing tool");

    const run = toolHandlers[toolName];
    if (!run) {
      throw new ValidationError(`Unknown tool: ${toolName}`);
    }
    // A few admin tools call the parameter environmentId. Accept the name every
    // other tool uses, so "environment" works everywhere.
    const aliased = aliasEnvironmentParameter(toolName, input);
    assertNoUnknownParameters(toolName, aliased);
    if (!isToolEnabledForFeatures(toolName, context.enabledFeatures)) {
      const requiredFeature = getRequiredFeatureForTool(toolName);
      const label = requiredFeature ? FEATURE_LABELS[requiredFeature] : "the required feature";
      throw new ValidationError(
        `${toolName} is disabled by your selected permission set. ` +
          `Re-run powerautomate-mcp --setup and choose a permission set that includes ${label}.`,
      );
    }
    // Names from list_environments must be usable wherever an id is (see
    // environment-resolution.ts); ids pass through untouched.
    // PAC Copilot requires an explicit Dataverse GUID/origin and uses its own
    // authentication. Do not reinterpret its HTTPS origin as an MCP display
    // name or require the separate Flow API sign-in to resolve it.
    const resolvedInput =
      Object.prototype.hasOwnProperty.call(pacCopilotHandlers, toolName) ||
      toolName === "get_copilot_agent_evaluation_connections" ||
      toolName === "create_copilot_mcp_connector" ||
      toolName === "list_copilot_agent_activity" ||
      toolName === "get_copilot_agent_session_summary" ||
      toolName === "get_copilot_agent_activity" ||
      toolName === "get_copilot_agent_evaluation_details"
        ? aliased
        : await resolveEnvironmentInput(context.flowApi, aliased);
    const result = await run(context, resolvedInput);
    const rich = typeof result === "string" ? { text: result } : result;

    // Cross-environment mistakes are the real risk of session switching:
    // switch to prod, forget, delete something meant for test. Every
    // destructive tool's output names the active environment while switched.
    const session = context.environmentSession;
    let text = rich.text;
    if (session && session.currentId !== session.bootId && isDestructiveToolName(toolName)) {
      text +=
        `\n\n⚠️ Active environment: "${session.currentName}" (${session.currentId}) — ` +
        `switched this session; your default is "${session.bootName}".`;
    }

    // Once per session: surface a known newer npm version where the user
    // actually is. Only on successful results — an update notice buried in an
    // error would read as part of the failure.
    const updateNotice = rich.isError === true ? null : consumeUpdateNotice();
    if (updateNotice) {
      text += `\n\n${updateNotice}`;
    }

    return {
      content: [{ type: "text", text }],
      ...(rich.structured ? { structuredContent: rich.structured } : {}),
      ...(rich.isError === true ? { isError: true } : {}),
    };
  } catch (error) {
    logger.error({ toolName, error }, "Tool execution failed");

    const errorMessage = formatErrorMessage(error);

    return {
      content: [{ type: "text", text: errorMessage }],
      isError: true,
    };
  }
}

/**
 * Format error messages in plain English with actionable guidance.
 * Exported for regression testing of the error-sanitization path.
 */
/**
 * A Zod validation error, recognised structurally rather than by `instanceof`.
 * Tools may parse with their own Zod instance, and an instanceof check across
 * two copies of the library silently fails.
 */
function isZodLikeError(
  error: unknown,
): error is { issues: { path?: (string | number)[]; message: string }[] } {
  if (error === null || typeof error !== "object") return false;
  const issues = (error as { issues?: unknown }).issues;
  if (!Array.isArray(issues) || issues.length === 0) return false;
  return issues.every(
    (issue) =>
      issue !== null &&
      typeof issue === "object" &&
      typeof (issue as { message?: unknown }).message === "string",
  );
}

export function formatErrorMessage(error: unknown): string {
  const conversationCaseMessage = getSafeCopilotConversationTestCaseErrorMessage(error);
  if (conversationCaseMessage !== undefined) return `Error: ${conversationCaseMessage}`;
  const evaluationConnectionsMessage = getSafeCopilotEvaluationConnectionsErrorMessage(error);
  if (evaluationConnectionsMessage !== undefined) return `Error: ${evaluationConnectionsMessage}`;
  if (error instanceof CopilotKnowledgeFileError) return `Error: ${error.message}`;
  if (error instanceof CopilotKnowledgeFileReadinessError) return `Error: ${error.message}`;
  if (error instanceof CopilotMonitorError) return `Error: ${error.message}`;
  const evaluationHttpError = formatCopilotEvaluationHttpError(error);
  if (evaluationHttpError !== undefined) return evaluationHttpError;
  // These scoped readers and guarded writes construct fixed, sanitized diagnostics.
  // Preserve them rather than suggesting unrelated Premium licensing or retry.
  if (
    error instanceof CopilotEvaluationConnectionsError ||
    error instanceof CopilotStudioEvaluationDetailsError ||
    error instanceof CopilotPermissionCatalogError ||
    error instanceof CopilotComponentUpdateError ||
    error instanceof CopilotConnectionReferenceUpdateError ||
    error instanceof CopilotMcpConnectorCreateError ||
    error instanceof CopilotActivityError ||
    error instanceof CopilotActivityDetailError
  ) {
    return `Error: ${sanitizeErrorMessage(error.message).slice(0, ERROR_DETAIL_LIMIT)}`;
  }
  // A confirmation prompt is authored here, not returned by an API, and it names
  // the very thing the caller asked about. Return it verbatim: the pattern
  // matching below would try to interpret it, and sanitizeErrorMessage would
  // redact the id the user needs in order to confirm.
  if (error instanceof ConfirmationRequiredError) {
    return error.message;
  }

  // A schema validation failure knows exactly WHICH field is wrong, but the
  // generic handling below reduced it to its code and message — so a tool with
  // a dozen parameters reported only "invalid_type: expected boolean, received
  // object", leaving the caller to guess which one. Name the field.
  if (isZodLikeError(error)) {
    const issues = error.issues.slice(0, 5).map((issue) => {
      const path = (issue.path ?? []).map((p) => String(p)).join(".");
      return path === "" ? issue.message : `${path}: ${issue.message}`;
    });
    const more =
      error.issues.length > issues.length ? ` (+${error.issues.length - issues.length} more)` : "";
    return `Invalid input — ${issues.join("; ")}${more}`;
  }

  if (error instanceof CanvasAuthoringError) {
    const message = sanitizeErrorMessage(error.message);
    const diagnostic = error.diagnostic
      ? sanitizeErrorMessage(error.diagnostic).replace(/\s+/g, " ").trim().slice(0, 2_000)
      : "";
    return `${error.code}: ${message}${diagnostic ? `\n\nDiagnostic: ${diagnostic}` : ""}`;
  }

  // Copilot API clients already distinguish missing scopes, consent, and user
  // roles. Keep their typed guidance before a noisy upstream 403/Forbidden body
  // can turn it into the generic Premium-license message or replace it entirely.
  // Read only the authored message here, never upstream details; all other API
  // families and untyped failures keep the existing formatter behavior.
  if (
    (error instanceof AuthorizationError || error instanceof AuthenticationError) &&
    /^(?:(?:Get|List|Start|Download|Set|Reassign|Execute|Quarantine|Remove) )?Copilot Studio\b/.test(
      error.message,
    )
  ) {
    const guidance = sanitizeErrorMessage(error.message).replace(/\s+/g, " ").trim();
    return `Error: ${guidance.slice(0, ERROR_DETAIL_LIMIT)}${guidance.length > ERROR_DETAIL_LIMIT ? "..." : ""}`;
  }

  let rawMessage: string;
  let apiDetails = "";

  // Extract message and details from error
  if (isPowerAutomateError(error)) {
    rawMessage = error.message;
    // Extract details from API response
    if (error.details) {
      apiDetails =
        typeof error.details === "string" ? error.details : JSON.stringify(error.details);
    }
  } else if (error instanceof Error) {
    rawMessage = error.message;
  } else {
    rawMessage = String(error);
  }

  // Defense-in-depth: error.message / error.details can carry record GUIDs, user
  // emails, paths, or tokens. This function reads error.details directly and
  // regex-extracts fragments into the user-facing string, bypassing
  // PowerAutomateError.toJSON's key stripping — so redact here before any of it
  // can surface to the MCP caller.
  rawMessage = sanitizeErrorMessage(rawMessage);
  apiDetails = sanitizeErrorMessage(apiDetails);

  // Combine message and details for pattern matching
  const fullMessage = rawMessage + " " + apiDetails;

  // Connection authorization failed
  if (fullMessage.includes("ConnectionAuthorizationFailed")) {
    const connectorMatch = fullMessage.match(/apis\/([^/]+)/);
    const connectorName = connectorMatch
      ? connectorMatch[1]!.replace("shared_", "").replace(/_/g, " ")
      : "required connector";

    return `Missing or invalid connection for "${connectorName}".

To fix this:
1. Go to make.powerautomate.com
2. Click Data > Connections
3. Click "+ New connection"
4. Search for "${connectorName}" and create it
5. Try again after creating the connection`;
  }

  // Invalid connection reference format
  if (
    fullMessage.includes("InvalidConnectionReference") ||
    fullMessage.includes("ConnectionNotFound")
  ) {
    return `Connection reference error. The connection ID may be incorrect.

Use list_connections to see your available connections and their IDs.`;
  }

  // Invalid template/definition
  if (fullMessage.includes("InvalidTemplate") || fullMessage.includes("InvalidDefinition")) {
    const detail = extractArmErrorMessage(fullMessage) ?? "The flow definition has errors";
    return `Invalid flow definition: ${detail.slice(0, ERROR_DETAIL_LIMIT)}`;
  }

  // Retired/unsupported API version — current releases only target supported
  // versions, so this almost always means the INSTALL is outdated (pre-0.13.4
  // sent the retired 2022-03-01-preview BAP version; seen in the wild
  // 2026-08-07 and misread as a product bug). Say so.
  if (fullMessage.includes("InvalidApiVersion")) {
    const detail =
      extractArmErrorMessage(fullMessage) ?? "Microsoft rejected the API version this call used";
    return `${detail.slice(0, ERROR_DETAIL_LIMIT)}

This usually means this installed copy (v${getInstalledVersion()}) is outdated — newer releases target Microsoft's current API versions. Quit your AI apps, then update:
  npm install -g powerautomate-mcp@latest
(or run: powerautomate-mcp --update)`;
  }

  // Missing environment filter
  if (fullMessage.includes("MissingEnvironmentFilter")) {
    return `Environment not specified. Use list_environments to see available environments.`;
  }

  // Authentication failed
  if (
    fullMessage.includes("Authentication failed") ||
    fullMessage.includes("401") ||
    fullMessage.includes("Unauthorized")
  ) {
    return `Authentication failed. Your session may have expired. Try reconnecting.`;
  }

  // A Dataverse privilege refusal names the exact privilege and entity, and the
  // generic branch below threw that away and speculated about a Premium licence
  // instead. Verified live: reading `agentconversationmessage` answered "is
  // missing prvReadagentconversationmessage privilege ... for entity
  // 'agentconversationmessage'", and the caller was told it might be a licence
  // problem — sending them to buy a SKU for what is a security-role gap. Name
  // what the server named; guessing a second cause is worse than saying one.
  const missingPrivilege = /is missing (prv\w+) privilege/i.exec(fullMessage);
  if (missingPrivilege) {
    const entity = /for entity '([^']+)'/i.exec(fullMessage)?.[1];
    const localized = /LocalizedName='([^']+)'/i.exec(fullMessage)?.[1];
    const on =
      entity === undefined
        ? ""
        : ` on '${entity}'${localized !== undefined && localized !== entity ? ` (${localized})` : ""}`;
    return (
      `Access denied: your account is missing the Dataverse privilege ${missingPrivilege[1]}${on}. ` +
      "This is a security-role gap, not a licence problem — ask an administrator to grant a role " +
      "that carries it. Retrying will not help."
    );
  }

  // Access denied
  if (
    fullMessage.includes("Access denied") ||
    fullMessage.includes("403") ||
    fullMessage.includes("Forbidden")
  ) {
    return `Access denied. You may not have permission or the required license (Premium) for this action.`;
  }

  // Flow not found
  if (
    fullMessage.includes("FlowNotFound") ||
    fullMessage.includes("ResourceNotFound") ||
    (fullMessage.includes("404") && fullMessage.includes("flow"))
  ) {
    return `Flow not found. It may have been deleted or you don't have access.`;
  }

  // Rate limiting
  if (
    fullMessage.includes("429") ||
    fullMessage.includes("TooManyRequests") ||
    fullMessage.includes("RateLimitExceeded")
  ) {
    return `Rate limited by Power Automate. Wait a moment and try again.`;
  }

  // Try to extract code and message from API response
  const codeMatch = fullMessage.match(/"code"\s*:\s*"([^"]+)"/);
  const extracted = extractArmErrorMessage(fullMessage);

  if (extracted) {
    const code = codeMatch ? codeMatch[1] : "";
    const msg = extracted.slice(0, ERROR_DETAIL_LIMIT);
    return code ? `${code}: ${msg}` : msg;
  }

  // Return cleaned up message
  const cleanMessage = rawMessage.replace(/\s+/g, " ").trim();
  return `Error: ${cleanMessage.slice(0, ERROR_DETAIL_LIMIT)}${cleanMessage.length > ERROR_DETAIL_LIMIT ? "..." : ""}`;
}

// Re-export individual tools and handlers for testing
// Phase 1
export { listFlowsTool, handleListFlows } from "./list-flows.js";
export { getFlowTool, handleGetFlow } from "./get-flow.js";
export { getRunsTool, handleGetRuns } from "./get-runs.js";
export { toggleFlowTool, handleToggleFlow } from "./toggle-flow.js";
// Phase 2
export { searchConnectorsTool, handleSearchConnectors } from "./search-connectors.js";
export { getActionSchemaTool, handleGetActionSchema } from "./get-action-schema.js";
// Phase 3
export { createFlowTool, handleCreateFlow } from "./create-flow.js";
export { updateFlowTool, handleUpdateFlow } from "./update-flow.js";
export { deleteFlowTool, handleDeleteFlow } from "./delete-flow.js";
export { validateFlowTool, handleValidateFlow } from "./validate-flow.js";
export { cloneFlowTool, handleCloneFlow } from "./clone-flow.js";
export { runFlowTool, handleRunFlow } from "./run-flow.js";
// Phase 4
export { listConnectionsTool, handleListConnections } from "./list-connections.js";
export { getRunActionsTool, handleGetRunActions } from "./get-run-actions.js";
export { getFlowPermissionsTool, handleGetFlowPermissions } from "./get-flow-permissions.js";
export { shareFlowTool, handleShareFlow } from "./share-flow.js";
// Phase 5
export { resubmitRunTool, handleResubmitRun } from "./resubmit-run.js";
export { cancelRunTool, handleCancelRun } from "./cancel-run.js";
export { listEnvironmentsTool, handleListEnvironments } from "./list-environments.js";
// Phase 6
export { listFlowVersionsTool, handleListFlowVersions } from "./list-flow-versions.js";
export { exportFlowTool, handleExportFlow } from "./export-flow.js";
export { listApprovalsTool, handleListApprovals } from "./list-approvals.js";
export { respondApprovalTool, handleRespondApproval } from "./respond-approval.js";
// Interactive planning and troubleshooting
export { planFlowTool, handlePlanFlow } from "./plan-flow.js";
export { diagnoseFlowTool, handleDiagnoseFlow } from "./diagnose-flow.js";
export { buildFlowTool, handleBuildFlow } from "./build-flow.js";
// Custom connector management
export {
  diagnoseConnectorAuthTool,
  listCustomConnectorsTool,
  handleListCustomConnectors,
  getCustomConnectorTool,
  handleGetCustomConnector,
  createCustomConnectorTool,
  handleCreateCustomConnector,
  updateCustomConnectorTool,
  handleUpdateCustomConnector,
  deleteCustomConnectorTool,
  handleDeleteCustomConnector,
  importOpenApiTool,
  handleImportOpenApi,
  planCustomConnectorTool,
  handlePlanCustomConnector,
} from "./custom-connectors.js";
// Excel helpers
export {
  searchExcelFilesTool,
  handleSearchExcelFiles,
  inspectExcelFileTool,
  handleInspectExcelFile,
} from "./excel-helpers.js";
// Power Apps tools
export { powerAppsTools } from "./powerapps-tools.js";
export { canvasAuthoringTools } from "./canvas-authoring-tools.js";
export { modelDrivenTools } from "./model-driven-tools.js";
// Admin tools
export { adminTools } from "./admin-tools.js";
// Solution tools
export { solutionTools } from "./solution-tools.js";
